import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Select, { components } from 'react-select';
import { talentService, locationService, companyService } from '../../../api/apiService';
import '../../../App.css';
import edit from '../../../images/edit.svg';
import closeButton from '../../../images/close.svg';

import calendarIconNew from  "./../../../images/calendar.svg";
import dropdownIcon from '../../../images/dropdown.svg';
import TalentCalendarApi from './TalentCalendarApi';
import { TalentCalendarProvider } from '../../../contexts/TalentCalendarContext';

// Add a helper function to format the timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Update the TruncatedContent component
const TruncatedContent = ({ content, type, isGoodPerformer = false, isBadPerformer = false, isExpanded = false, onToggle }) => {
  if (!content) return null;

  let displayContent;
  if (type === "notes") {
    displayContent = content;
  } else if (type === "companies") {
    // Handle company data with timestamps and ratings
    if (isExpanded) {
      // When expanded, show each company on a separate line with full details
      if (Array.isArray(content)) {
        return (
          <div onClick={onToggle} style={{
            color: isGoodPerformer ? "#37b24d" : isBadPerformer ? "#ff6b6b" : "inherit",
            cursor: 'pointer',
            display: 'block',
            width: '100%',
            fontSize: '0.85em' // Smaller font for companies
          }}>
            {content.map((c, index) => {
              const companyName = c.label || c.name || c;
              const timestamp = c.timestamp ? ` (${formatTimestamp(c.timestamp)})` : '';
              const rating = c.rating ? ` [Rating ${c.rating}]` : '';
              return (
                <div key={index} style={{ 
                  marginBottom: '2px',
                  padding: '1px 2px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '2px',
                  fontSize: '0.83rem', // Even smaller font for individual companies
                  fontWeight: '300' // Remove bold styling
                }}>
                  {companyName}{rating}{timestamp}
                </div>
              );
            })}
          </div>
        );
      } else {
        displayContent = content;
      }
    } else {
      // When collapsed, show truncated list
      displayContent = Array.isArray(content)
        ? content.map(c => {
            const companyName = c.label || c.name || c;
            const timestamp = c.timestamp ? ` (${formatTimestamp(c.timestamp)})` : '';
            const rating = c.rating ? ` [Rating ${c.rating}]` : '';
            return `${companyName}${rating}${timestamp}`;
          }).join(", ")
        : content;
    }
  } else if (type === "locations") {
    // Handle location data
    displayContent = Array.isArray(content)
      ? content.map(loc => loc.label || loc.name || loc.short || loc).join(", ")
      : content;
  } else {
    displayContent = Array.isArray(content)
      ? content.map(c => c.label || c.name || c).join(", ")
      : content;
  }

  const style = {
    color: isGoodPerformer ? "#37b24d" : isBadPerformer ? "#ff6b6b" : "inherit",
    cursor: 'pointer',
    display: 'inline-block',
    width: '100%',
    fontSize: type === "notes" ? '0.8rem' : '0.85em' // Even smaller font for notes
  };

  return (
    <div onClick={onToggle} style={style}>
      {isExpanded ? displayContent : (displayContent.length > 40 ? `${displayContent.substring(0, 40)}...` : displayContent)}
    </div>
  );
};

// Add helper function to check company matches
const isCompanyMatch = (companies, targetCompany) => {
  if (!companies || !Array.isArray(companies)) {
    return false;
  }
  
  return companies.some((company) => {
    if (typeof company === 'string') {
      return company.toLowerCase().includes(targetCompany.toLowerCase());
    } else if (company && typeof company === 'object') {
      const label = company.label || company.name || '';
      return label.toLowerCase().includes(targetCompany.toLowerCase());
    }
    return false;
  });
};

// Add helper function to get performance ranking
const getPerformanceRanking = (talent, targetCompany) => {
  if (!talent || !targetCompany) {
    return 3; // Default ranking
  }
  
  const targetCompanyLower = targetCompany.toLowerCase();
  const isGoodMatch = isCompanyMatch(talent.goodPerformanceCompany, targetCompanyLower);
  const isTryoutMatch = isCompanyMatch(talent.tryOutCompany, targetCompanyLower);
  const isBadMatch = isCompanyMatch(talent.badPerformanceCompany, targetCompanyLower);

  if (isGoodMatch) return 1;
  if (isTryoutMatch) return 2;
  if (isBadMatch) return 4;
  return 3;
};

// Add DropdownIndicator component
const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <div style={{ width: "16px", margin: "0" }}>
          <svg
            version="1.1"
            id="Isolationsmodus"
            x="0px"
            y="0px"
            viewBox="0 0 322.9 257.78"
            shapeRendering="geometricPrecision"
          >
            <path
              xmlns="http://www.w3.org/2000/svg"
              d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z"
            />
          </svg>
        </div>
      </components.DropdownIndicator>
    )
  );
};

// Add notes options
const notesOption = [
  {value: 'LEGER', label:'LEGER'},
  {value: 'HÄNGER', label:'HÄNGER'},
  {value: 'BÜSTE', label:'BÜSTE'},
  {value: 'ACC', label:'ACC'},
  {value: 'On Model', label:'On Model'},
  {value: 'Free text', label:'Free text'}
];

// Add positions options
const positions = [
  { value: "Hair & Make-Up", label: "Hair & Make-Up" },
  { value: "Art Direktion", label: "Art Direktion" },
  { value: "Fot", label: "Foto" },
  { value: "Foto Assist", label: "Foto Assist" },
  { value: "Video", label: "Video" },
  { value: "Digi-OP", label: "Digi-OP" },
  { value: "Styling", label: "Styling" },
  { value: "Styling Assist", label: "Styling Assist" },
  { value: "LOGISTIK / WARENAUFBEREITUNG", label: "LOGISTIK / WARENAUFBEREITUNG" },
  { value: "PRODUCER", label: "PRODUCER" },
  { value: "Art Buying", label: "Art Buying" }, 
  { value: "Account Management", label: "Account Management" },
  { value: "AI Content Production", label: "AI Content Production" },
  { value: 'Development', label: 'Development' },
];

// Add set options
const set = [
  { value: 'On Model', label: 'On Model' },
  { value: 'On Produkt', label: 'On Produkt' },
  { value: 'digital Production', label: 'digital Production' }
];

// Add performance type options
const performanceTypeOptions = [
  { value: 'good', label: 'Good Performer' },
  { value: 'bad', label: 'Bad Performer' },
  { value: 'tryout', label: 'Try Out' }
];

function TalentListApi() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [talents, setTalents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTalent, setCurrentTalent] = useState(null);
  const [currentName, setCurrentName] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [currentFunction, setCurrentFunction] = useState("");
  const [currentSurname, setCurrentSurname] = useState("");
  const [currentProductLeadNotes, setCurrentProductLeadNotes] = useState("");
  const [currentProductLeadNotesType, setCurrentProductLeadNotesType] = useState(null);
  const [currentPhone, setCurrentPhone] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");
  const [selectedGoodCompanies, setSelectedGoodCompanies] = useState([]);
  const [selectedBadCompanies, setSelectedBadCompanies] = useState([]);
  const [selectedtryoutCompanies, setSelectedtryoutCompanies] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]); // For editing talent locations
  const [filterLocations, setFilterLocations] = useState([]); // For filtering talents
  const [locationsModified, setLocationsModified] = useState(false); // Track if locations have been modified
  const [selectedPosition, setselectedPosition] = useState(null);
  const [bereich, setBereich] = useState(null);
  const [lanoid, setlanoid] = useState('');
  
  // Filters
  const [selectedType, setSelectedType] = useState("");
  const [selectedFunction, setSelectedFunction] = useState("");
  const [selectedSet, setSelectedSet] = useState(null);
  const [locations, setLocations] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isExpanded, setIsExpanded] = useState(true);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedPerformanceCompany, setSelectedPerformanceCompany] = useState(null);
  const [performanceType, setPerformanceType] = useState(null);
  
  // Bulk selection states
  const [selectedTalentIds, setSelectedTalentIds] = useState(new Set());
  const [isLinkingTalents, setIsLinkingTalents] = useState(false);
  
  // Export modal states
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState({
    name: true,
    email: true,
    phone: false,
    function: false,
    type: false,
    rate: false,
    locations: false,
    notes: false,
    productLeadNotes: false,
    lanoId: false
  });

  // Pagination
  const TALENTS_PER_PAGE = 20;

  // Wrapper function to handle location changes and track modifications
  const handleLocationSelectionChange = (newLocations, isUserAction = true) => {
    setSelectedLocations(newLocations);
    if (isUserAction) {
      setLocationsModified(true);
    }
  };

  // Custom style for selects
  const customStyles = {
    control: (base) => ({
      ...base,
      minWidth: "150px",
      borderColor: "black",
      borderWidth: "1px",
      boxShadow: "none",
      margin: "5px",
      borderRadius: "0px",
      color: "black",
      fontWeight: "700",
      height: "auto",
      textTransform: "uppercase",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "700",
    }),
    input: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "700",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "black" : "inherit",
      "&:hover": { backgroundColor: state.isSelected ? "black" : "#f7f7f7" },
    }),
  };

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
  };

  // Fetch locations from API
  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations(1, 1000); // Fetch all locations
      const locationsData = response?.data?.locations || response?.data || [];
      
      const formattedLocations = locationsData
        .map(location => ({
          value: location.short, // Use short code as value
          label: location.short, // Display only short code
          short: location.short
        }))
        .filter(location => location.value && location.short) // Filter out entries without short codes
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setLocationOptions(formattedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Set fallback locations if API fails
      setLocationOptions([
        { value: 'HH', label: 'HH', short: 'HH' },
        { value: 'BER', label: 'BER', short: 'BER' },
        { value: 'MUC', label: 'MUC', short: 'MUC' }
      ]);
    }
  };

  // Add state for all talents
  const [allTalents, setAllTalents] = useState([]);

  // Update the fetchTalents function to fetch all data once
  const fetchTalents = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch all talents without filters
      const response = await talentService.getAllTalents(1, 1000);
      
      // Handle response format
      const talentsData = response?.data?.talents || response?.data || [];
      
      if (Array.isArray(talentsData)) {
        // Fetch all company relationships in a single batch request
        const talentIds = talentsData.map(t => t.id).filter(Boolean);
        let companyMap = {};
        try {
          const batchResponse = await talentService.getTalentCompaniesBatch(talentIds);
          companyMap = batchResponse?.data?.data || {};
        } catch (error) {
          console.error('Could not fetch batch company data:', error);
        }

        const formatLocations = (locations) =>
          (locations || []).map(loc => {
            if (typeof loc === 'string') return loc;
            if (typeof loc === 'object' && loc.short) return loc.short;
            return loc;
          }).filter(Boolean);

        const enhancedTalentsData = talentsData.map(talent => {
          const companyData = companyMap[talent.id] || {};
          return {
            ...talent,
            goodPerformanceCompany: companyData.goodPerformanceCompany || [],
            badPerformanceCompany: companyData.badPerformanceCompany || [],
            tryOutCompany: companyData.tryOutCompany || [],
            locations: formatLocations(talent.locations)
          };
        });
        
        // Sort talents alphabetically by name
        const sortedTalents = enhancedTalentsData.sort((a, b) => {
          const nameA = (a.name || a.Name || '').toLowerCase();
          const nameB = (b.name || b.Name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setAllTalents(sortedTalents);
        setHasMore(false); // Since we're fetching all at once
      } else {
        console.error('Unexpected response format:', response);
        setAllTalents([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching talents:', error);
      setAllTalents([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [locationOptions]);

  // Fetch companies from API
  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies(1, 1000); // Fetch all companies
      const companiesData = response?.data?.companies || response?.data || [];
      
      const formattedCompanies = companiesData
        .map(company => ({
          value: company.id,
          label: company.name
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setCompanyOptions(formattedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Add filter function
  const applyFilters = useCallback(() => {
    let filtered = [...allTalents];

    // Apply search filter - search by name and email with space normalization
    if (search && search.trim()) {
      try {
        // Normalize spaces in search term (replace multiple spaces with single space)
        const searchLower = search.toLowerCase().trim().replace(/\s+/g, ' ');
        
        filtered = filtered.filter(talent => {
          if (!talent) return false;
          
          // Normalize spaces in talent name and email
          const name = (talent.name || talent.Name || '').toLowerCase().replace(/\s+/g, ' ');
          const email = (talent.email || talent.E_Mail || '').toLowerCase().replace(/\s+/g, ' ');
          
          // Search in both name and email
          return name.includes(searchLower) || email.includes(searchLower);
        });
      } catch (error) {
        console.error('Error in search filter:', error);
        // If search fails, return original filtered array
      }
    }

    // Apply function filter
    if (selectedFunction) {
      filtered = filtered.filter(talent => 
        (talent.function || talent.Funktion) === selectedFunction
      );
    }

    // Apply type filter
    if (selectedType) {
      filtered = filtered.filter(talent => 
        (talent.type || talent.bereich || talent.Bereich) === selectedType
      );
    }

    // Apply set filter
    if (selectedSet?.value) {
      filtered = filtered.filter(talent => 
        talent.set === selectedSet.value
      );
    }

    // Apply location filter
    if (filterLocations.length > 0) {
      const selectedLocationShorts = filterLocations.map(loc => loc.value); // Since we're using short codes as values
      filtered = filtered.filter(talent => {
        const talentLocations = talent.locations || [];
        // Since talent locations are now just short code strings, we can compare directly
        return selectedLocationShorts.some(selectedShort => 
          talentLocations.includes(selectedShort)
        );
      });
      
      // Location filter applied
    }

    // Apply performance company filter
    if (selectedPerformanceCompany && performanceType) {
      const companyValue = selectedPerformanceCompany.value;
      const companyLabel = selectedPerformanceCompany.label;
      
      filtered = filtered.filter(talent => {
        let companyList = [];
        
        if (performanceType.value === 'good') {
          companyList = talent.goodPerformanceCompany || [];
        } else if (performanceType.value === 'bad') {
          companyList = talent.badPerformanceCompany || [];
        } else if (performanceType.value === 'tryout') {
          companyList = talent.tryOutCompany || [];
        }
        
        // Check if the company exists in the selected performance list
        return companyList.some(company => {
          if (typeof company === 'string') {
            return company === companyLabel;
          } else if (company && typeof company === 'object') {
            return company.value === companyValue || company.label === companyLabel;
          }
          return false;
        });
      });
    }

    // Sort filtered talents alphabetically by name
    const sortedFiltered = filtered.sort((a, b) => {
      const nameA = (a.name || a.Name || '').toLowerCase();
      const nameB = (b.name || b.Name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    setTalents(sortedFiltered);
  }, [allTalents, search, selectedFunction, selectedType, selectedSet, filterLocations, selectedPerformanceCompany, performanceType]);

  // Update filter handlers to use client-side filtering
  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const handleTypeChange = useCallback((option) => {
    setSelectedType(option ? option.value : "");
  }, []);

  const handleFunctionChange = useCallback((option) => {
    setSelectedFunction(option ? option.value : "");
  }, []);

  const handleLocationChange = useCallback((options) => {
    setFilterLocations(options || []);
  }, []);

  const handleSetChange = useCallback((option) => {
    setSelectedSet(option);
  }, []);

  const handlePerformanceCompanyChange = useCallback((option) => {
    setSelectedPerformanceCompany(option);
  }, []);

  const handlePerformanceTypeChange = useCallback((option) => {
    setPerformanceType(option);
    // Clear company selection when performance type changes
    if (!option) {
      setSelectedPerformanceCompany(null);
    }
  }, []);

  // Apply filters whenever any filter changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Clear selection of talents that are no longer visible after filtering
  useEffect(() => {
    if (selectedTalentIds.size > 0) {
      const visibleTalentIds = new Set(talents.map(talent => talent.id));
      const updatedSelection = new Set([...selectedTalentIds].filter(id => visibleTalentIds.has(id)));
      
      if (updatedSelection.size !== selectedTalentIds.size) {
        setSelectedTalentIds(updatedSelection);
      }
    }
  }, [talents, selectedTalentIds]);

  // Initialize data
  useEffect(() => {
    fetchLocations();
    fetchCompanies();
    fetchTalents();
  }, []);

  useEffect(() => {
    fetchTalents();
  }, [fetchTalents]);
  
  // Effect to expand all rows by default
  useEffect(() => {
    const allTalentIds = talents.map(talent => talent.id);
    setExpandedRows(new Set(allTalentIds));
    setIsExpanded(true);
  }, [talents]);

  // Toggle expanding/collapsing all rows
  const toggleAllRows = () => {
    if (isExpanded) {
      setExpandedRows(new Set());
    } else {
      const allTalentIds = talents.map(talent => talent.id);
      setExpandedRows(new Set(allTalentIds));
    }
    setIsExpanded(!isExpanded);
  };

  // Toggle individual row expansion
  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Edit talent function
  const editTalent = async (talent) => {
    // Debug removed to reduce console noise
    setIsEditing(true);
    setCurrentTalent(talent);
    setLocationsModified(false); // Reset locations modified flag when starting to edit
    setCurrentEmail(talent.email || talent.E_Mail);
    setCurrentName(talent.name || talent.Name);
    setCurrentFunction(talent.function || talent.Funktion);
    setCurrentSurname(talent.notes || talent.Notiz_Setcast);
    setCurrentProductLeadNotes(talent.productLeadNotes || talent.Product_Lead_Notes || talent.notesProductLeads || '');
    setCurrentProductLeadNotesType(talent.productLeadNotesType || talent.Product_Lead_Notes_Type || talent.notesProductLeadsdd || null);
    setCurrentPhone(talent.phone || talent.Telefon);
    setCurrentPrice(talent.rate || talent.Tagessatz);
    setlanoid(talent.lanoId || talent.Lano_ID);
    setBereich({ label: talent.type || talent.bereich || talent.Bereich, value: talent.type || talent.bereich || talent.Bereich });
    setselectedPosition({ label: talent.function || talent.Funktion, value: talent.function || talent.Funktion });
    
    try {
      // First try to fetch company data from the new endpoint
      const response = await talentService.getTalentCompanies(talent.id);
      // The companies are in response.data.data
      if (response && response.data && response.data.data) {
        const { goodPerformanceCompany, badPerformanceCompany, tryOutCompany } = response.data.data;
        
        // Format retrieved company data for Select components
        const formattedGoodCompanies = goodPerformanceCompany?.map(company => ({
          label: company.label,
          value: company.value,
          timestamp: company.timestamp || new Date().toISOString()
        })) || [];
        
        const formattedBadCompanies = badPerformanceCompany?.map(company => ({
          label: company.label,
          value: company.value,
          timestamp: company.timestamp || new Date().toISOString()
        })) || [];
        
        const formattedTryoutCompanies = tryOutCompany?.map(company => ({
          label: company.label,
          value: company.value,
          timestamp: company.timestamp || new Date().toISOString()
        })) || [];
        
        // Company data formatted for select components
        
        setSelectedGoodCompanies(formattedGoodCompanies);
        setSelectedBadCompanies(formattedBadCompanies);
        setSelectedtryoutCompanies(formattedTryoutCompanies);
      } else {
        // Fallback to talent object data for companies
        const formattedGoodCompanies = ensureCompanyFormat(talent.goodPerformanceCompany || []);
        const formattedBadCompanies = ensureCompanyFormat(talent.badPerformanceCompany || []);
        const formattedTryoutCompanies = ensureCompanyFormat(talent.tryOutCompany || []);
        
        setSelectedBadCompanies(formattedBadCompanies);
        setSelectedGoodCompanies(formattedGoodCompanies);
        setSelectedtryoutCompanies(formattedTryoutCompanies);
      }
    } catch (error) {
      console.error('Error fetching company relationships:', error);
      const formattedGoodCompanies = ensureCompanyFormat(talent.goodPerformanceCompany || []);
      const formattedBadCompanies = ensureCompanyFormat(talent.badPerformanceCompany || []);
      const formattedTryoutCompanies = ensureCompanyFormat(talent.tryOutCompany || []);
      
      setSelectedBadCompanies(formattedBadCompanies);
      setSelectedGoodCompanies(formattedGoodCompanies);
      setSelectedtryoutCompanies(formattedTryoutCompanies);
    }
    
    // Convert talent locations (short codes) to Select component format
    const talentLocationOptions = (talent.locations || []).map(locationShort => {
      // Find the matching location option
      const matchingOption = locationOptions.find(opt => opt.value === locationShort);
      return matchingOption || { value: locationShort, label: locationShort, short: locationShort };
    });
    handleLocationSelectionChange(talentLocationOptions, false); // false = not a user action, just initialization
  };

  // Helper function to ensure company data has consistent format
  const ensureCompanyFormat = (companies) => {
    return companies.map(company => {
      if (typeof company === 'string') {
        return {
          label: company,
          value: company,
          timestamp: new Date().toISOString()
        };
      } else if (typeof company === 'object') {
        return {
          label: company.label || company.name || '',
          value: company.value || company.id || '',
          rating: company.rating || undefined, // Preserve rating
          timestamp: company.timestamp || new Date().toISOString()
        };
      }
      return company;
    });
  };

  // Function to update a single talent in the local state
  const updateSingleTalent = useCallback((talentId, updatedData) => {
    setAllTalents(prevTalents => {
      return prevTalents.map(talent => {
        if (talent.id === talentId) {
          return { ...talent, ...updatedData };
        }
        return talent;
      });
    });
  }, []);

  const updateTalentDetails = async () => {
    if (currentTalent) {
      try {
        // Get the original talent data first
        const originalTalentResponse = await talentService.getTalentById(currentTalent.id);
        const originalTalent = originalTalentResponse.data;
        
        // Compare basic info fields and only include changed ones
        const changedBasicInfo = {};
        
        // Helper function to check if a field has changed
        const hasFieldChanged = (newValue, oldValue, fieldName) => {
          return newValue !== oldValue;
        };
        
        // Check each field for changes
        if (hasFieldChanged(currentName, originalTalent.name || originalTalent.Name, 'name')) {
          changedBasicInfo.Name = currentName;
        }
        
        if (hasFieldChanged(selectedPosition?.label, originalTalent.function || originalTalent.Funktion, 'function')) {
          changedBasicInfo.Funktion = selectedPosition.label;
        }
        
        if (hasFieldChanged(currentEmail, originalTalent.email || originalTalent.E_Mail, 'email')) {
          changedBasicInfo.E_Mail = currentEmail;
        }
        
        if (hasFieldChanged(currentPrice, originalTalent.rate || originalTalent.Tagessatz, 'rate')) {
          changedBasicInfo.Tagessatz = currentPrice;
        }
        
        if (hasFieldChanged(currentSurname, originalTalent.notes || originalTalent.Notiz_Setcast, 'notes')) {
          changedBasicInfo.Notiz_Setcast = currentSurname;
        }
        
        if (hasFieldChanged(currentProductLeadNotes, originalTalent.productLeadNotes || originalTalent.Product_Lead_Notes || originalTalent.notesProductLeads, 'productLeadNotes')) {
          changedBasicInfo.notesProductLeads = currentProductLeadNotes;
        }
        
        // Handle Select component value comparison for notes type
        const originalNotesType = originalTalent.productLeadNotesType || originalTalent.Product_Lead_Notes_Type || originalTalent.notesProductLeadsdd;
        const currentNotesTypeValue = currentProductLeadNotesType?.value || currentProductLeadNotesType;
        const originalNotesTypeValue = originalNotesType?.value || originalNotesType;
        
        if (hasFieldChanged(currentNotesTypeValue, originalNotesTypeValue, 'productLeadNotesType')) {
          changedBasicInfo.notesProductLeadsdd = currentProductLeadNotesType;
        }
        
        if (hasFieldChanged(currentPhone, originalTalent.phone || originalTalent.Telefon, 'phone')) {
          changedBasicInfo.Telefon = currentPhone;
        }
        
        if (hasFieldChanged(lanoid, originalTalent.lanoId || originalTalent.Lano_ID, 'lanoId')) {
          changedBasicInfo.Lano_ID = lanoid;
        }
        
        if (hasFieldChanged(bereich?.label, originalTalent.type || originalTalent.bereich || originalTalent.Bereich, 'type')) {
          changedBasicInfo.Bereich = bereich.label;
        }

        // Note: Removed additional field mappings that were causing issues
        // These fields (firebaseId, parentId, AP, etc.) are not editable in the form
        // and were being set to undefined, causing the API to remove location data
        
        // Compare locations only if they have been modified by the user
        if (locationsModified) {
          const originalLocationShorts = (originalTalent.locations || []).map(loc => 
            typeof loc === 'string' ? loc : (loc.short || loc.value)
          ).sort();
          const newLocationShorts = (selectedLocations || []).map(loc => loc.value).sort();
          
          if (JSON.stringify(originalLocationShorts) !== JSON.stringify(newLocationShorts)) {
            // Send the short codes as an array of strings to the API
            changedBasicInfo.locations = newLocationShorts;
          }
        }
        // If locations were not modified, we don't include them in changedBasicInfo
        // PATCH request will only update the provided fields, preserving existing locations
        
        // Filter out undefined values before sending to API
        const cleanedBasicInfo = {};
        Object.keys(changedBasicInfo).forEach(key => {
          if (changedBasicInfo[key] !== undefined && changedBasicInfo[key] !== null) {
            cleanedBasicInfo[key] = changedBasicInfo[key];
          }
        });

        // Only update basic info if there are changes
        if (Object.keys(cleanedBasicInfo).length > 0) {
          // console.log('Debug - SENDING TO API:', cleanedBasicInfo);
          // console.log('Debug - API request contains locations?', 'locations' in cleanedBasicInfo);
          await talentService.updateTalent(currentTalent.id, cleanedBasicInfo);
        } else {
          // console.log('Debug - NO CHANGES, not sending API request');
        }
        
        // Helper function to find companies that are in newList but not in originalList
        const findNewCompanies = (newList, originalList) => {
          return newList.filter(newCompany => {
            return !originalList.some(origCompany => 
              origCompany.value === newCompany.value
            );
          });
        };
        
        // Helper function to find companies that are in originalList but not in newList
        const findRemovedCompanies = (newList, originalList) => {
          return originalList.filter(origCompany => {
            return !newList.some(newCompany => 
              newCompany.value === origCompany.value
            );
          });
        };

        // Helper function to get the final list after adding new and removing old companies
        const getFinalList = (originalList, newCompanies, removedCompanies) => {
          // Start with original list
          const result = [...originalList];
          
          // Remove items that should be removed
          const afterRemoval = result.filter(item => 
            !removedCompanies.some(removed => removed.value === item.value)
          );
          
          // Add new items with current timestamp, preserving any existing ratings
          return [
            ...afterRemoval,
            ...newCompanies.map(company => ({
              label: company.label,
              value: company.value,
              rating: company.rating || undefined, // Preserve rating if it exists
              timestamp: new Date().toISOString()
            }))
          ];
        };

        // Get the original companies from the API
        const originalResponse = await talentService.getTalentCompanies(currentTalent.id);
        
        // Initialize company change tracking variables
        let newGoodCompanies = [];
        let removedGoodCompanies = [];
        let newBadCompanies = [];
        let removedBadCompanies = [];
        let newTryoutCompanies = [];
        let removedTryoutCompanies = [];
        let originalData = {};
        
        // Check if we have original data to compare against
        if (originalResponse && originalResponse.data) {
          originalData = originalResponse.data;
          
          // Find new and removed companies for each category
          newGoodCompanies = findNewCompanies(selectedGoodCompanies, originalData.goodPerformanceCompany || []);
          removedGoodCompanies = findRemovedCompanies(selectedGoodCompanies, originalData.goodPerformanceCompany || []);
          
          newBadCompanies = findNewCompanies(selectedBadCompanies, originalData.badPerformanceCompany || []);
          removedBadCompanies = findRemovedCompanies(selectedBadCompanies, originalData.badPerformanceCompany || []);
          
          newTryoutCompanies = findNewCompanies(selectedtryoutCompanies, originalData.tryOutCompany || []);
          removedTryoutCompanies = findRemovedCompanies(selectedtryoutCompanies, originalData.tryOutCompany || []);
          
          // Only send updates if there are changes
          if (
            newGoodCompanies.length > 0 || removedGoodCompanies.length > 0 ||
            newBadCompanies.length > 0 || removedBadCompanies.length > 0 ||
            newTryoutCompanies.length > 0 || removedTryoutCompanies.length > 0
          ) {
            // Prepare the final data for the API
            const updatedCompanyData = {
              goodPerformanceCompany: getFinalList(originalData.goodPerformanceCompany || [], newGoodCompanies, removedGoodCompanies),
              badPerformanceCompany: getFinalList(originalData.badPerformanceCompany || [], newBadCompanies, removedBadCompanies),
              tryOutCompany: getFinalList(originalData.tryOutCompany || [], newTryoutCompanies, removedTryoutCompanies)
            };
            
            // Send the update
            await talentService.updateTalentCompanies(currentTalent.id, updatedCompanyData);
          }
        }
        
        // Update the talent in local state instead of refetching all talents
        const finalLocations = changedBasicInfo.locations !== undefined ? changedBasicInfo.locations : currentTalent.locations;
        
        const updatedTalentData = {
          // Map the updated basic info back to the talent object format
          name: changedBasicInfo.Name || currentTalent.name || currentTalent.Name,
          Name: changedBasicInfo.Name || currentTalent.name || currentTalent.Name,
          function: changedBasicInfo.Funktion || currentTalent.function || currentTalent.Funktion,
          Funktion: changedBasicInfo.Funktion || currentTalent.function || currentTalent.Funktion,
          email: changedBasicInfo.E_Mail || currentTalent.email || currentTalent.E_Mail,
          E_Mail: changedBasicInfo.E_Mail || currentTalent.email || currentTalent.E_Mail,
          rate: changedBasicInfo.Tagessatz || currentTalent.rate || currentTalent.Tagessatz,
          Tagessatz: changedBasicInfo.Tagessatz || currentTalent.rate || currentTalent.Tagessatz,
          notes: changedBasicInfo.Notiz_Setcast || currentTalent.notes || currentTalent.Notiz_Setcast,
          Notiz_Setcast: changedBasicInfo.Notiz_Setcast || currentTalent.notes || currentTalent.Notiz_Setcast,
          productLeadNotes: changedBasicInfo.notesProductLeads || currentTalent.productLeadNotes || currentTalent.Product_Lead_Notes || currentTalent.notesProductLeads,
          Product_Lead_Notes: changedBasicInfo.notesProductLeads || currentTalent.productLeadNotes || currentTalent.Product_Lead_Notes || currentTalent.notesProductLeads,
          notesProductLeads: changedBasicInfo.notesProductLeads || currentTalent.productLeadNotes || currentTalent.Product_Lead_Notes || currentTalent.notesProductLeads,
          productLeadNotesType: changedBasicInfo.notesProductLeadsdd || currentTalent.productLeadNotesType || currentTalent.Product_Lead_Notes_Type || currentTalent.notesProductLeadsdd,
          Product_Lead_Notes_Type: changedBasicInfo.notesProductLeadsdd || currentTalent.productLeadNotesType || currentTalent.Product_Lead_Notes_Type || currentTalent.notesProductLeadsdd,
          notesProductLeadsdd: changedBasicInfo.notesProductLeadsdd || currentTalent.productLeadNotesType || currentTalent.Product_Lead_Notes_Type || currentTalent.notesProductLeadsdd,
          phone: changedBasicInfo.Telefon || currentTalent.phone || currentTalent.Telefon,
          Telefon: changedBasicInfo.Telefon || currentTalent.phone || currentTalent.Telefon,
          lanoId: changedBasicInfo.Lano_ID || currentTalent.lanoId || currentTalent.Lano_ID,
          Lano_ID: changedBasicInfo.Lano_ID || currentTalent.lanoId || currentTalent.Lano_ID,
          type: changedBasicInfo.Bereich || currentTalent.type || currentTalent.bereich || currentTalent.Bereich,
          bereich: changedBasicInfo.Bereich || currentTalent.type || currentTalent.bereich || currentTalent.Bereich,
          Bereich: changedBasicInfo.Bereich || currentTalent.type || currentTalent.bereich || currentTalent.Bereich,
          locations: finalLocations,
        };

        // Update company relationships if they were changed
        if (
          newGoodCompanies.length > 0 || removedGoodCompanies.length > 0 ||
          newBadCompanies.length > 0 || removedBadCompanies.length > 0 ||
          newTryoutCompanies.length > 0 || removedTryoutCompanies.length > 0
        ) {
          updatedTalentData.goodPerformanceCompany = getFinalList(originalData.goodPerformanceCompany || [], newGoodCompanies, removedGoodCompanies);
          updatedTalentData.badPerformanceCompany = getFinalList(originalData.badPerformanceCompany || [], newBadCompanies, removedBadCompanies);
          updatedTalentData.tryOutCompany = getFinalList(originalData.tryOutCompany || [], newTryoutCompanies, removedTryoutCompanies);
        }

        // Update the single talent in local state
        updateSingleTalent(currentTalent.id, updatedTalentData);
        
        setIsEditing(false);
        setSelectedLocations([]); // Clear edit locations after successful save
        setLocationsModified(false); // Reset locations modified flag
        setCurrentProductLeadNotesType(null); // Clear notes type after successful save
      } catch (error) {
        console.error('Error updating talent:', error);
        alert('Error updating talent. Please try again.');
      }
    }
  };

  // Duplicate talent function using new backend endpoint
  const duplicateTalent = async (originalTalent) => {
    try {
      // Use the new backend endpoint that handles everything
      const response = await talentService.duplicateTalent(originalTalent.id);
      
      // If the response contains the new talent data, add it to local state
      if (response && response.data && response.data.newTalent) {
        const newTalent = response.data.newTalent;
        setAllTalents(prevTalents => {
          // Add the new talent and sort the list
          const updatedTalents = [...prevTalents, newTalent].sort((a, b) => {
            const nameA = (a.name || a.Name || '').toLowerCase();
            const nameB = (b.name || b.Name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
          return updatedTalents;
        });
      } else {
        // Fallback: refresh only if we don't get the new talent data
        fetchTalents();
      }
      
      // Show success message
      const originalName = originalTalent.name || originalTalent.Name;
      alert(`Talent "${originalName}" has been successfully duplicated! The duplicate has been created with empty function, rate, notes, and ratings, and both talents are now linked as related.`);
      
    } catch (error) {
      console.error('Error duplicating talent:', error);
      alert('Error duplicating talent: ' + (error.response?.data?.message || error.message));
    }
  };

  // Delete talent function
  const deleteTalent = async (talent) => {
    const talentName = talent.name || talent.Name;
    
    // Show confirmation dialog
    const confirmMessage = `Are you sure you want to permanently delete "${talentName}"?\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Call the delete API
      await talentService.deleteTalent(talent.id);
      
      // Remove the talent from local state
      setAllTalents(prevTalents => {
        return prevTalents.filter(t => t.id !== talent.id);
      });
      
      // Close the editing form
      setIsEditing(false);
      setCurrentTalent(null);
      setSelectedLocations([]);
      setLocationsModified(false);
      setCurrentProductLeadNotesType(null);
      
      // Show success message
      alert(`Talent "${talentName}" has been successfully deleted.`);
      
    } catch (error) {
      console.error('Error deleting talent:', error);
      alert('Error deleting talent: ' + (error.response?.data?.message || error.message || 'An unexpected error occurred'));
    }
  };

  // Add a handler for calendar button clicks
  const handleCalendarClick = (talentId) => {
    setSelectedTalent(selectedTalent === talentId ? null : talentId);
  };

  // Bulk selection handlers
  const handleTalentSelection = (talentId, isSelected) => {
    setSelectedTalentIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(talentId);
      } else {
        newSet.delete(talentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedTalentIds.size === talents.length) {
      // Deselect all
      setSelectedTalentIds(new Set());
    } else {
      // Select all visible talents
      setSelectedTalentIds(new Set(talents.map(talent => talent.id)));
    }
  };

  const clearSelection = () => {
    setSelectedTalentIds(new Set());
  };

  // Show export options modal
  const showExportOptions = () => {
    if (talents.length === 0) {
      alert('No talents to export');
      return;
    }
    setShowExportModal(true);
  };

  // Handle export field selection
  const handleExportFieldChange = (fieldName, checked) => {
    setSelectedExportFields(prev => ({
      ...prev,
      [fieldName]: checked
    }));
  };

  // Export talents to CSV with selected fields
  const exportTalentsToCSV = () => {
    // Check if at least one field is selected
    const selectedFields = Object.entries(selectedExportFields).filter(([key, value]) => value);
    if (selectedFields.length === 0) {
      alert('Please select at least one field to export');
      return;
    }

    // Prepare CSV data
    const csvData = [];
    
    // Define field mappings
    const fieldMappings = {
      name: { header: 'Name', getValue: (talent) => talent.name || talent.Name || '' },
      email: { header: 'Email', getValue: (talent) => talent.email || talent.E_Mail || '' },
      phone: { header: 'Phone', getValue: (talent) => talent.phone || talent.Telefon || '' },
      function: { header: 'Function', getValue: (talent) => talent.function || talent.Funktion || '' },
      type: { header: 'Type', getValue: (talent) => talent.type || talent.bereich || talent.Bereich || '' },
      rate: { header: 'Rate', getValue: (talent) => talent.rate || talent.Tagessatz || '' },
      locations: { header: 'Locations', getValue: (talent) => Array.isArray(talent.locations) ? talent.locations.join(', ') : (talent.locations || '') },
      notes: { header: 'Setcast Notes', getValue: (talent) => talent.notes || talent.Notiz_Setcast || '' },
      productLeadNotes: { header: 'Product Lead Notes', getValue: (talent) => {
        const notes = talent.productLeadNotes || talent.Product_Lead_Notes || talent.notesProductLeads || '';
        const type = talent.productLeadNotesType?.label || talent.Product_Lead_Notes_Type?.label || talent.notesProductLeadsdd?.label || 
                    talent.productLeadNotesType || talent.Product_Lead_Notes_Type || talent.notesProductLeadsdd || '';
        return type ? `${notes} - ${type}` : notes;
      }},
      lanoId: { header: 'Lano ID', getValue: (talent) => talent.lanoId || talent.Lano_ID || '' }
    };
    
    // Add header row
    const headers = selectedFields.map(([fieldName]) => fieldMappings[fieldName].header);
    csvData.push(headers);
    
    // Add talent data
    talents.forEach(talent => {
      const row = selectedFields.map(([fieldName]) => {
        const value = fieldMappings[fieldName].getValue(talent);
        return value.toString();
      });
      csvData.push(row);
    });
    
    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => `"${field.toString().replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename with current date and filter info
    const currentDate = new Date().toISOString().split('T')[0];
    let filename = `talents_${currentDate}`;
    
    // Add filter info to filename if filters are applied
    const activeFilters = [];
    if (search) activeFilters.push('search');
    if (selectedType) activeFilters.push('type');
    if (selectedFunction) activeFilters.push('function');
    if (filterLocations.length > 0) activeFilters.push('location');
    if (selectedPerformanceCompany && performanceType) activeFilters.push('performance');
    
    if (activeFilters.length > 0) {
      filename += `_filtered_${activeFilters.join('_')}`;
    }
    
    filename += '.csv';
    link.setAttribute('download', filename);
    
    // Trigger download
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Close modal and show success message
    setShowExportModal(false);
    alert(`Exported ${talents.length} talent${talents.length !== 1 ? 's' : ''} to ${filename}`);
  };

  // Link selected talents using bulk relationships API
  const linkSelectedTalents = async () => {
    if (selectedTalentIds.size < 2) {
      alert('Please select at least 2 talents to link together.');
      return;
    }

    const talentIds = Array.from(selectedTalentIds);
    const talentNames = talents
      .filter(talent => selectedTalentIds.has(talent.id))
      .map(talent => talent.name || talent.Name)
      .join(', ');

    const confirmMessage = `Are you sure you want to link these ${talentIds.length} talents together?\n\nTalents: ${talentNames}\n\nThis will create bidirectional relationships so their schedules will be visible to each other.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLinkingTalents(true);
    
    try {
      const response = await talentService.createBulkRelationships(talentIds);
      
      if (response && response.data && response.data.success) {
        const details = response.data.details;
        let successMessage = `✅ Successfully linked ${details.validTalentsFound} talents!\n\n`;
        successMessage += `📊 Relationships created: ${details.relationshipsCreated}\n`;
        successMessage += `📊 Relationships already existed: ${details.relationshipsExisted}\n`;
        
        if (details.notFoundIds && details.notFoundIds.length > 0) {
          successMessage += `\n⚠️ Note: Some talent IDs were not found: ${details.notFoundIds.join(', ')}`;
        }
        
        alert(successMessage);
        
        // Clear selection after successful linking
        clearSelection();
        
        // Note: Relationships are not displayed in the main list, so no need to refresh
        // The relationships will be visible in the calendar view when opened
      } else {
        throw new Error(response?.data?.message || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error linking talents:', error);
      alert(`❌ Failed to link talents: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLinkingTalents(false);
    }
  };

  // Add options derived from allTalents
  const typeOptions = useMemo(() => {
    return Array.from(new Set(allTalents.map(talent => 
      talent.type || talent.bereich || talent.Bereich
    )))
    .filter(Boolean)
    .map(type => ({
      value: type,
      label: type
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  }, [allTalents]);

  const functionOptions = useMemo(() => {
    return Array.from(new Set(allTalents.map(talent => 
      talent.function || talent.Funktion
    )))
    .filter(Boolean)
    .map(func => ({
      value: func,
      label: func
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  }, [allTalents]);

  const setOptions = useMemo(() => [
    { value: 'On Model', label: 'On Model' },
    { value: 'On Produkt', label: 'On Produkt' },
    { value: 'digital Production', label: 'digital Production' },
  ], []);

  // Render component
  return (
    <div className="content-wrapper page">
    <div className="content-wrapper">
        <h1>Talent List (API)</h1>
 
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "40px",
          }}
        >
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
            className="inputtext search"
          />
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              className="big-button"
              onClick={() => navigate('/admin/addtalent')}
              style={{ textDecoration: "none", alignSelf: "start" }}
            >
              Add Talent
            </button>
            <button
              className="big-button"
              onClick={() => navigate('/admin/add-company')}
              style={{ textDecoration: "none", alignSelf: "start" }}
            >
              Add Company
            </button>
            <button
              className="big-button"
              onClick={() => navigate('/admin/company-group')}
              style={{ textDecoration: "none", alignSelf: "start" }}
            >
              Company Groups
            </button>
            <button
              className="big-button"
              onClick={showExportOptions}
              style={{ textDecoration: "none", alignSelf: "start" }}
            >
              Export CSV ({talents.length})
            </button>
          </div>
        </div>

        <div
          style={{ display: "flex", marginTop: "10px" }}
          className="content-wrapper-talent-list"
        >
          <div style={{ marginRight: "10px" }}>
            <Select
              value={typeOptions.find(option => option.value === selectedType)}
              onChange={handleTypeChange}
              options={typeOptions}
              isClearable
              placeholder="Select Type"
              styles={customStyles}
            />
          </div>
          <div>
            <Select
              value={functionOptions.find(option => option.value === selectedFunction)}
              onChange={handleFunctionChange}
              options={functionOptions}
              isClearable
              placeholder="Select Function"
              styles={customStyles}
            />
          </div>
          <div>
            <Select
              isMulti
              value={filterLocations}
              onChange={handleLocationChange}
              options={locationOptions}
              isClearable
              placeholder="Select Location"
              styles={customStyles}
            />
          </div>

          <div>
            <Select
              value={performanceType}
              onChange={handlePerformanceTypeChange}
              options={performanceTypeOptions}
              isClearable
              placeholder="Performance Type"
              styles={customStyles}
            />
          </div>
          <div>
            <Select
              value={selectedPerformanceCompany}
              onChange={handlePerformanceCompanyChange}
              options={companyOptions}
              isClearable
              placeholder={performanceType ? `${performanceType.label} Company` : "Select Company"}
              isDisabled={!performanceType}
              styles={customStyles}
            />
          </div>
          <div style={{ marginLeft: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
            <button 
              className='button'
              onClick={toggleAllRows}
            >
              {isExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
        </div>

        {/* Bulk Operations Section */}
        {selectedTalentIds.size > 0 && (
          <div style={{
            marginTop: "20px",
            padding: "15px",
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <strong>{selectedTalentIds.size} talent{selectedTalentIds.size !== 1 ? 's' : ''} selected</strong>
              <span style={{ color: "#666", fontSize: "14px" }}>
                ({talents.filter(t => selectedTalentIds.has(t.id)).map(t => t.name || t.Name).join(', ')})
              </span>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button 
                className="button"
                onClick={linkSelectedTalents}
                disabled={isLinkingTalents || selectedTalentIds.size < 2}
                style={{
                  backgroundColor: selectedTalentIds.size >= 2 ? "#28a745" : "#6c757d",
                  color: "white"
                }}
              >
                {isLinkingTalents ? "Linking..." : `Link ${selectedTalentIds.size} Talents`}
              </button>
              <button 
                className="button"
                onClick={clearSelection}
                style={{ backgroundColor: "#dc3545", color: "white" }}
              >
                Clear Selection
              </button>
                         </div>
           </div>
         )}
        </div>
      
      <div className="content-wrapper content-wrapper-talent-list">
        <div className="talent-list">
          <div className="talent-item-header" style={{ maxWidth: "1800px" }}>
            <div style={{ flexBasis: "40px" }}>
              <input
                type="checkbox"
                checked={selectedTalentIds.size === talents.length && talents.length > 0}
                onChange={handleSelectAll}
                title="Select/Deselect All"
                style={{ cursor: "pointer" }}
              />
            </div>
            <div style={{ flexBasis: "260px" }}>Name</div>
            <div style={{ flexBasis: "150px" }}>Type</div>
            <div style={{ flexBasis: "200px" }}>Function</div>
            <div style={{ flexBasis: "350px" }}>Email</div>
            <div style={{ flexBasis: "380px" }}>Notes</div>
            <div style={{ flexBasis: "150px" }}>Day Rate</div>
            <div style={{ flexBasis: "200px" }}>Phone</div>
            <div style={{ flexBasis: "200px" }}>Location</div>
            <div style={{ flexBasis: "200px", minWidth: "200px" }}>Good Performer</div>
            <div style={{ flexBasis: "150px", minWidth: "150px" }}>Try Out</div>
            <div style={{ flexBasis: "150px", minWidth: "150px" }}>Bad Performer</div>
            <div style={{ flexBasis: "50px" }}></div>
          </div>
          
          <div style={{ height: "50px" }}></div>
          
          {isLoading ? (
            <div className="loading">Loading talents...</div>
          ) : talents.length === 0 ? (
            <div style={{ 
              padding: "40px", 
              textAlign: "center", 
              color: "#666",
              fontSize: "1.1rem"
            }}>
              {search ? `No talents found matching "${search}"` : "No talents found"}
            </div>
          ) : (
            talents.map((talent) => (
              <div key={talent.id} className="talent-item">
                <div className="talent-item-firstline">
                  <div style={{ flexBasis: "40px" }}>
                    <input
                      type="checkbox"
                      checked={selectedTalentIds.has(talent.id)}
                      onChange={(e) => handleTalentSelection(talent.id, e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                  <div style={{ flexBasis: "260px", fontSize: '0.9rem' }}>{talent.name || talent.Name}</div>
                  <div style={{ flexBasis: "150px", fontSize: '0.9rem' }}>{talent.type || talent.bereich || talent.Bereich}</div>
                  <div style={{ flexBasis: "200px", fontSize: '0.9rem' }}>{talent.function || talent.Funktion}</div>
                  <div style={{ flexBasis: "350px", fontSize: '0.9rem' }}>{talent.email || talent.E_Mail}</div>
                  <div style={{ flexBasis: "380px", fontSize: '0.8rem' }}>

                    {expandedRows.has(talent.id) ? 
                      (
                        <div>
                          {(talent.notes || talent.Notiz_Setcast) && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong style={{ fontSize: '0.75em', color: '#666' }}>Setcast Notes:</strong>
                              <div style={{ 
                                padding: '4px 8px', 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '4px',
                                borderLeft: '3px solid #007bff',
                                fontSize: '0.8rem'
                              }}>
                                {talent.notes || talent.Notiz_Setcast}
                              </div>
                            </div>
                          )}
                          {(talent.productLeadNotes || talent.Product_Lead_Notes || talent.notesProductLeads) && (
                            <div>
                              <strong style={{ fontSize: '0.75em', color: '#666' }}>Product Lead Notes:</strong>
                              <div style={{ 
                                padding: '4px 8px', 
                                backgroundColor: '#fff3cd', 
                                borderRadius: '4px',
                                borderLeft: '3px solid #ffc107',
                                fontSize: '0.8rem'
                              }}>
                                {talent.productLeadNotes || talent.Product_Lead_Notes || talent.notesProductLeads}
                                {(talent.productLeadNotesType || talent.Product_Lead_Notes_Type || talent.notesProductLeadsdd) && 
                                  ` - ${(talent.productLeadNotesType?.label || talent.Product_Lead_Notes_Type?.label || talent.notesProductLeadsdd?.label || talent.productLeadNotesType || talent.Product_Lead_Notes_Type || talent.notesProductLeadsdd)}`
                                }
                              </div>
                            </div>
                          )}
                          {!(talent.notes || talent.Notiz_Setcast) && !(talent.productLeadNotes || talent.Product_Lead_Notes || talent.notesProductLeads) && 
                            <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.8rem' }}>No notes</span>
                          }
                        </div>
                      ) : 
                      <TruncatedContent 
                        content={
                          [
                            (talent.notes || talent.Notiz_Setcast) && `Setcast: ${talent.notes || talent.Notiz_Setcast}`,
                            (talent.productLeadNotes || talent.Product_Lead_Notes || talent.notesProductLeads) && `Product Lead: ${talent.productLeadNotes || talent.Product_Lead_Notes || talent.notesProductLeads}${(talent.productLeadNotesType || talent.Product_Lead_Notes_Type || talent.notesProductLeadsdd) ? ` - ${(talent.productLeadNotesType?.label || talent.Product_Lead_Notes_Type?.label || talent.notesProductLeadsdd?.label || talent.productLeadNotesType || talent.Product_Lead_Notes_Type || talent.notesProductLeadsdd)}` : ''}`
                          ].filter(Boolean).join(' | ') || 'No notes'
                        }
                        type="notes" 
                        isExpanded={expandedRows.has(talent.id)}
                        onToggle={() => toggleRow(talent.id)}
                      />}
                  </div>
                  <div style={{ flexBasis: "150px", fontSize: '0.9rem' }}>{talent.rate || talent.Tagessatz}</div>
                  <div style={{ flexBasis: "200px", fontSize: '0.9rem' }}>{talent.phone || talent.Telefon}</div>
                  <div style={{ flexBasis: "200px", fontSize: '0.9rem' }}>
                    <TruncatedContent 
                      content={talent.locations || []} 
                      type="locations"
                      isExpanded={expandedRows.has(talent.id)}
                      onToggle={() => toggleRow(talent.id)}
                    />
                  </div>
                  <div style={{ flexBasis: "200px", minWidth: "200px", wordWrap: "break-word", overflowWrap: "break-word", height: "auto", maxHeight: "none", overflow: "visible" }}>
                    {talent.goodPerformanceCompany && Array.isArray(talent.goodPerformanceCompany) ? (
                      expandedRows.has(talent.id) ? (
                        talent.goodPerformanceCompany.map((company, index) => {
                          const companyLabel = typeof company === 'string' 
                            ? company 
                            : company.label || company.name || '';
                          const companyRating = company.rating;
                          const companyTimestamp = company.timestamp;
                          
                          return (
                            <div key={`${companyLabel}-${index}`} style={{ 
                              marginBottom: '4px',
                              padding: '4px 6px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '3px',
                              color: '#37b24d',
                              fontSize: '0.85rem',
                              fontWeight: '400',
                              lineHeight: '1.3',
                              wordWrap: 'break-word'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '500' }}>{companyLabel}</span>
                                {companyRating && (
                                  <span style={{ 
                                    fontSize: '0.75em',
                                    padding: '2px 6px',
                                    backgroundColor: '#d4edda',
                                    borderRadius: '12px',
                                    color: '#495057',
                                    fontWeight: '400'
                                  }}>
                                    Rating {companyRating}
                                  </span>
                                )}
                              </div>
                              {companyTimestamp && (
                                <div style={{ 
                                  fontSize: '0.75em',
                                  color: '#666',
                                  marginTop: '2px',
                                  paddingTop: '2px',
                                  borderTop: '1px solid #ddd',
                                  fontWeight: '400'
                                }}>
                                  Added: {formatDate(companyTimestamp)}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <TruncatedContent 
                          content={talent.goodPerformanceCompany || []} 
                          type="companies"
                          isGoodPerformer={true}
                          isExpanded={expandedRows.has(talent.id)}
                          onToggle={() => toggleRow(talent.id)}
                        />
                      )
                    ) : ''}
                  </div>
                  <div style={{ flexBasis: "150px", minWidth: "150px", wordWrap: "break-word", overflowWrap: "break-word", height: "auto", maxHeight: "none", overflow: "visible" }}>
                    {talent.tryOutCompany && Array.isArray(talent.tryOutCompany) ? (
                      expandedRows.has(talent.id) ? (
                        talent.tryOutCompany.map((company, index) => {
                          const companyLabel = typeof company === 'string' 
                            ? company 
                            : company.label || company.name || '';
                          const companyRating = company.rating;
                          const companyTimestamp = company.timestamp;
                          
                          return (
                            <div key={`${companyLabel}-${index}`} style={{ 
                              marginBottom: '4px',
                              padding: '4px 6px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '3px',
                              fontSize: '0.85rem',
                              fontWeight: '400',
                              lineHeight: '1.3',
                              wordWrap: 'break-word'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '500' }}>{companyLabel}</span>
                                {companyRating && (
                                  <span style={{ 
                                    fontSize: '0.75em',
                                    padding: '2px 6px',
                                    backgroundColor: '#e9ecef',
                                    borderRadius: '12px',
                                    color: '#495057',
                                    fontWeight: '400'
                                  }}>
                                    Rating {companyRating}
                                  </span>
                                )}
                              </div>
                              {companyTimestamp && (
                                <div style={{ 
                                  fontSize: '0.75em',
                                  color: '#666',
                                  marginTop: '2px',
                                  paddingTop: '2px',
                                  borderTop: '1px solid #ddd',
                                  fontWeight: '400'
                                }}>
                                  Added: {formatDate(companyTimestamp)}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <TruncatedContent 
                          content={talent.tryOutCompany || []} 
                          type="companies"
                          isExpanded={expandedRows.has(talent.id)}
                          onToggle={() => toggleRow(talent.id)}
                        />
                      )
                    ) : ''}
                  </div>
                  <div style={{ flexBasis: "150px", minWidth: "150px", wordWrap: "break-word", overflowWrap: "break-word", height: "auto", maxHeight: "none", overflow: "visible" }}>
                    {talent.badPerformanceCompany && Array.isArray(talent.badPerformanceCompany) ? (
                      expandedRows.has(talent.id) ? (
                        talent.badPerformanceCompany.map((company, index) => {
                          const companyLabel = typeof company === 'string' 
                            ? company 
                            : company.label || company.name || '';
                          const companyRating = company.rating;
                          const companyTimestamp = company.timestamp;
                          
                          return (
                            <div key={`${companyLabel}-${index}`} style={{ 
                              marginBottom: '4px',
                              padding: '4px 6px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '3px',
                              color: '#ff6b6b',
                              fontSize: '0.85rem',
                              fontWeight: '400',
                              lineHeight: '1.3',
                              wordWrap: 'break-word'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '500' }}>{companyLabel}</span>
                                {companyRating && (
                                  <span style={{ 
                                    fontSize: '0.75em',
                                    padding: '2px 6px',
                                    backgroundColor: '#f8d7da',
                                    borderRadius: '12px',
                                    color: '#495057',
                                    fontWeight: '400'
                                  }}>
                                    Rating {companyRating}
                                  </span>
                                )}
                              </div>
                              {companyTimestamp && (
                                <div style={{ 
                                  fontSize: '0.75em',
                                  color: '#666',
                                  marginTop: '2px',
                                  paddingTop: '2px',
                                  borderTop: '1px solid #ddd',
                                  fontWeight: '400'
                                }}>
                                  Added: {formatDate(companyTimestamp)}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <TruncatedContent 
                          content={talent.badPerformanceCompany || []} 
                          type="companies"
                          isBadPerformer={true}
                          isExpanded={expandedRows.has(talent.id)}
                          onToggle={() => toggleRow(talent.id)}
                        />
                      )
                    ) : ''}
                  </div>
                  <div style={{ flexBasis: "50px", display: "flex", gap: "8px" }}>
                    <button
                      className="toggle-buttons"
                      onClick={() => toggleRow(talent.id)}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '16px',
                          transform: expandedRows.has(talent.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease'
                        }}
                      >
                        <img src={dropdownIcon} alt="dropdown" style={{ width: '100%', height: 'auto' }} />
                      </span>
                    </button>
                    <button 
                      className="edit-button"
                      onClick={() => editTalent(talent)}
                    >
                      <img src={edit} alt="Edit" className="edit-container" />
                    </button>
                    <button
                      className="edit-button"
                      onClick={() => handleCalendarClick(talent.id)}
                    >
                      <img
                        src={selectedTalent === talent.id ? closeButton : calendarIconNew}
                        alt={selectedTalent === talent.id ? "Close Calendar" : "Open Calendar"}
                        className="edit-container"
                      />
                    </button>
                  </div>
                </div>
                
                {selectedTalent === talent.id && (
                  <div className="talent-calendar-container" style={{ width: '100%', marginTop: '20px', height: '550px', overflow: 'hidden' }}>
                    <TalentCalendarProvider talentId={talent.id}>
                      <TalentCalendarApi
                        talentId={talent.id}
                        relatedTalents={talent.relatedTalent || []}
                      />
                    </TalentCalendarProvider>
                  </div>
                )}

                {isEditing && currentTalent.id === talent.id && (
                  <div className="talent-item-edit-form">
                    <div className="edit-form-grid">
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Name</span>
                        <input
                          className="inputtext"
                          value={currentName}
                          onChange={(e) => setCurrentName(e.target.value)}
                          style={{ margin: '5px' }}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Function</span>
                        <Select
                          components={{ DropdownIndicator }}
                          options={positions}
                          onChange={setselectedPosition}
                          placeholder="Function"
                          value={selectedPosition}
                          styles={customStyles}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Locations</span>
                        <Select
                          isMulti
                          value={selectedLocations}
                          onChange={handleLocationSelectionChange}
                          options={locationOptions}
                          isClearable
                          placeholder="Locations"
                          styles={customStyles}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Email</span>
                        <input
                          className="inputtext"
                          value={currentEmail}
                          onChange={(e) => setCurrentEmail(e.target.value)}
                          style={{ margin: '5px' }}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Setcast Notes</span>
                        <input
                          className="inputtext"
                          value={currentSurname}
                          onChange={(e) => setCurrentSurname(e.target.value)}
                          style={{ margin: '5px' }}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Product Lead Notes</span>
                        <input
                          className="inputtext"
                          value={currentProductLeadNotes}
                          onChange={(e) => setCurrentProductLeadNotes(e.target.value)}
                          style={{ margin: '5px' }}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Notes Type</span>
                        <Select
                          value={currentProductLeadNotesType}
                          onChange={setCurrentProductLeadNotesType}
                          options={notesOption}
                          isClearable
                          placeholder="Notes Type"
                          styles={customStyles}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Lano ID</span>
                        <input
                          className="inputtext"
                          value={lanoid}
                          onChange={(e) => setlanoid(e.target.value)}
                          style={{ margin: '5px' }}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Phone</span>
                        <input
                          className="inputtext"
                          value={currentPhone}
                          onChange={(e) => setCurrentPhone(e.target.value)}
                          style={{ margin: '5px' }}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Rate in euro - only numbers</span>
                        <input
                          className="inputtext"
                          type="text"
                          value={currentPrice}
                          onChange={(e) => {
                            // Remove any non-numeric characters
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setCurrentPrice(value);
                          }}
                          placeholder="Enter rate"
                          style={{ margin: '5px' }}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Type</span>
                        <Select
                          value={bereich}
                          onChange={setBereich}
                          options={set}
                          isClearable
                          styles={customStyles}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Good Performance Companies</span>
                        <Select
                          isMulti
                          value={selectedGoodCompanies}
                          onChange={setSelectedGoodCompanies}
                          options={companyOptions}
                          isClearable
                          placeholder="Good Performance Companies"
                          styles={customStyles}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Bad Performance Companies</span>
                        <Select
                          isMulti
                          value={selectedBadCompanies}
                          onChange={setSelectedBadCompanies}
                          options={companyOptions}
                          isClearable
                          placeholder="Bad Performance Companies"
                          styles={customStyles}
                        />
                      </div>
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Try Out Companies</span>
                        <Select
                          isMulti
                          value={selectedtryoutCompanies}
                          onChange={setSelectedtryoutCompanies}
                          options={companyOptions}
                          isClearable
                          placeholder="Try Out Companies"
                          styles={customStyles}
                        />
                      </div>
                    </div>
                    <div className="edit-form-actions" style={{margin:'5px'}}>
                      <button className="button" onClick={updateTalentDetails}>
                        Save
                      </button>
                      <button 
                        className="button" 
                        onClick={() => duplicateTalent(currentTalent)}
                      >
                        Duplicate
                      </button>
                      <button 
                        className="button" 
                        onClick={() => deleteTalent(currentTalent)}
                        style={{ backgroundColor: "#dc3545", borderColor: "#dc3545", color: "white" }}
                      >
                        Delete
                      </button>
                      <button className="button" onClick={() => {
                        setIsEditing(false);
                        setSelectedLocations([]); // Clear edit locations when canceling
                        setLocationsModified(false); // Reset locations modified flag
                        setCurrentProductLeadNotesType(null); // Clear notes type when canceling
                      }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Export Options Modal */}
      {showExportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Export Options</h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Select the fields you want to include in the CSV export ({talents.length} talents):
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              {Object.entries(selectedExportFields).map(([fieldName, isSelected]) => {
                const fieldLabels = {
                  name: 'Name',
                  email: 'Email Address',
                  phone: 'Phone Number',
                  function: 'Function',
                  type: 'Type',
                  rate: 'Day Rate',
                  locations: 'Locations',
                  notes: 'Setcast Notes',
                  productLeadNotes: 'Product Lead Notes',
                  lanoId: 'Lano ID'
                };
                
                return (
                  <div key={fieldName} style={{
                    display: 'inline-block',
                  
                    alignItems: 'left',
                    marginBottom: '10px',
                    marginRight: '10px',
                    padding: '8px',
                    backgroundColor: isSelected ? '#f0f8ff' : '#f8f9fa',
                    borderRadius: '4px',
                    border: isSelected ? '1px solid #007bff' : '1px solid #dee2e6'
                  }}>
                    <input
                      type="checkbox"
                      id={`export-${fieldName}`}
                      checked={isSelected}
                      onChange={(e) => handleExportFieldChange(fieldName, e.target.checked)}
                      style={{ marginRight: '10px', cursor: 'pointer' }}
                    />
                    <label 
                      htmlFor={`export-${fieldName}`}
                      style={{ 
                        cursor: 'pointer', 
                        fontWeight: isSelected ? '500' : 'normal',
                        color: isSelected ? '#007bff' : 'inherit'
                      }}
                    >
                      {fieldLabels[fieldName]}
                    </label>
                  </div>
                );
              })}
            </div>

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              borderTop: '1px solid #dee2e6',
              paddingTop: '20px'
            }}>
              <button
                className="big-button"
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </button>
              <button
                className="big-button"
                onClick={() => {
                  // Select all fields
                  const allSelected = {};
                  Object.keys(selectedExportFields).forEach(key => {
                    allSelected[key] = true;
                  });
                  setSelectedExportFields(allSelected);
                }}
              >
                Select All
              </button>
              <button
                className="big-button"
                onClick={exportTalentsToCSV}
                disabled={Object.values(selectedExportFields).every(v => !v)}
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TalentListApi; 
